
import React from "react";
import { NotificationProductData } from "@/types/notifications";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, Lock, Search, Info } from "lucide-react";

interface ProductItemProps {
  product: NotificationProductData;
  index: number;
  updateProductField: (index: number, field: keyof NotificationProductData, value: string | number) => void;
  removeProduct: (index: number) => void;
  handleInputFocus: (index: number, field: string) => void;
  handleInputBlur: () => void;
}

const ProductItem = ({
  product,
  index,
  updateProductField,
  removeProduct,
  handleInputFocus,
  handleInputBlur
}: ProductItemProps) => {
  // Check if the product SKU contains "KX", making fields editable if true
  const isKXProduct = product.product_sku?.toLowerCase().includes('kx');
  // Fields are read-only unless it's a KX product
  const isReadOnly = !isKXProduct;

  return (
    <div className="p-4 border rounded-md relative">
      <div className="absolute top-2 right-2">
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          onClick={() => removeProduct(index)}
          className="h-8 w-8 p-0"
        >
          <Trash2 className="h-4 w-4 text-red-500" />
          <span className="sr-only">Verwijderen</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label htmlFor={`product_name_${index}`} className="flex items-center">
            <div className="flex-1">
              Productnaam <span className="text-red-500">*</span>
            </div>
            {isReadOnly && (
              <Lock className="h-3 w-3 inline-block ml-1 text-gray-400" />
            )}
          </Label>
          <Input
            id={`product_name_${index}`}
            value={product.product_name}
            onChange={(e) => updateProductField(index, 'product_name', e.target.value)}
            required
            readOnly={isReadOnly}
            className={isReadOnly ? "bg-gray-100 text-gray-600" : ""}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`product_eenheid_${index}`} className="flex items-center">
            <div className="flex-1">
              Eenheid <span className="text-red-500">*</span>
            </div>
            {isReadOnly && (
              <Lock className="h-3 w-3 inline-block ml-1 text-gray-400" />
            )}
          </Label>
          <Input
            id={`product_eenheid_${index}`}
            value={product.product_eenheid}
            onChange={(e) => updateProductField(index, 'product_eenheid', e.target.value)}
            required
            readOnly={isReadOnly}
            className={isReadOnly ? "bg-gray-100 text-gray-600" : ""}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2 relative">
          <Label htmlFor={`product_sku_${index}`} className="flex items-center">
            <div className="flex-1">
              Artikelnummer <span className="text-red-500">*</span>
            </div>
            <div className="text-xs text-blue-600 flex items-center">
              <Search className="h-3 w-3 mr-1" /> Zoeken vereist
            </div>
          </Label>
          <Input
            id={`product_sku_${index}`}
            value={product.product_sku}
            onChange={(e) => updateProductField(index, 'product_sku', e.target.value)}
            onFocus={() => handleInputFocus(index, 'product_sku')}
            onBlur={handleInputBlur}
            required
            placeholder="Zoek op artikelnummer"
          />
        </div>

        <div className="space-y-2 relative">
          <Label htmlFor={`product_bc_${index}`} className="flex items-center">
            <div className="flex-1">
              BC <span className="text-red-500">*</span>
            </div>
            {isReadOnly && (
              <Lock className="h-3 w-3 inline-block ml-1 text-gray-400" />
            )}
          </Label>
          <Input
            id={`product_bc_${index}`}
            value={product.product_bc}
            onChange={(e) => updateProductField(index, 'product_bc', e.target.value)}
            required
            readOnly={isReadOnly}
            className={isReadOnly ? "bg-gray-100 text-gray-600" : ""}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`product_quantity_${index}`}>
            Aantal <span className="text-red-500">*</span>
          </Label>
          <Input
            id={`product_quantity_${index}`}
            type="number"
            min="1"
            value={product.product_quantity}
            onChange={(e) => updateProductField(index, 'product_quantity', parseInt(e.target.value) || 1)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`product_color_${index}`}>Kleur op maat</Label>
          <Input
            id={`product_color_${index}`}
            value={product.product_color || ''}
            onChange={(e) => updateProductField(index, 'product_color', e.target.value)}
          />
        </div>
      </div>
      
      {isKXProduct ? (
        <div className="mt-3 text-xs flex items-start p-2 bg-amber-50 border border-amber-200 rounded-md text-amber-700">
          <Info className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
          <span>KX-artikelnummer gedetecteerd: Productnaam, Eenheid en BC zijn handmatig bewerkbaar.</span>
        </div>
      ) : (
        <div className="mt-3 text-xs text-gray-500 italic">
          Productnaam, Eenheid, en BC worden automatisch ingevuld door te zoeken op artikelnummer
        </div>
      )}
    </div>
  );
};

export default ProductItem;
